import { graphql, HeadFC, PageProps, withPrefix } from "gatsby"
import { FC, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"

// components
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs"
import { Layout } from "../components/Layout/Layout"
import CategorySideNav, {
  Props as SideNavProps,
  FeaturedCategoryItem
} from "../components/CategorySideNav/CategorySideNav"
import Subtopic, {
  Props as SubTopicProps,
  singleArticle
} from "../components/Subtopics/Subtopic"
import GenericSideNav, {
  PropItem as GenericPropItem
} from "../components/GenericSideNav/GenericSideNav"
import { HeadUniversal } from "../components/HeadUniversal/HeadUniversal"

// utils
import { RootState } from "../store/store"
import { events } from "../utils/heap/tracking_events"
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../utils/seo/seo_utils"
import { assignLangCode } from "../utils/general_utils"
import { renderRichText } from "../utils/contentful/render_rich_text"

// types
import { SingleHrefLangMetaDataSet } from "../../gatsby-node"

// styles
import {
  contentWrapper,
  header,
  mobileWrapper,
  topicPageWrapper
} from "./Topics.module.scss"
import { HrefLangTags } from "../components/HrefLangTags/HrefLangTags"
import { CanonicalTag } from "../components/CanonicalTag/CanonicalTag"
import { setAudience } from "../store/slices/audience.slice"

export type PageContext = {
  categoryMenuSlug: string
  metaData: {
    hrefLangMetaData: SingleHrefLangMetaDataSet[]
  }
  node_locale: string
}

const TopicPageTemplate: FC<PageProps<Queries.CategoriesQuery>> = (
  pageProps
) => {
  if (typeof window === "undefined") {
    return null
  }

  const data = pageProps?.data
  const prefixedBasePath = withPrefix(
    `/${pageProps?.pageContext["node_locale"]?.toLowerCase()}`
  )

  const audience: string = useSelector((State: RootState) => State.audience)
  const userProfile = useSelector((state: RootState) => state.userProfile)
  const currentLangCode = useSelector(
    (state: RootState) => state.metaData.langCode
  )

  const dispatch = useDispatch()

  useEffect(() => {
    assignLangCode(currentLangCode, dispatch)
  }, [])

  useEffect(() => {
    if (userProfile) {
      events?.userProfileData("User Profile", userProfile)
    }
  }, [userProfile])

  const subTopics: SubTopicProps[] = useMemo(() => {
    return data?.contentfulCategoryMenu?.subcategories.map((sCat) => {
      const articleList: singleArticle[] = sCat?.articleList?.map((art) => {
        return {
          articleId: art.articleId,
          title: art.title,
          slug: art.slug
        }
      })
      return {
        articleList: articleList,
        subcategoryHeader: sCat.subcategoryHeader
      }
    })
  }, [data?.contentfulCategoryMenu?.subcategories])

  const audienceCategoryData = useMemo<
    Pick<
      Queries.ContentfulFeaturedCategories,
      | "featuredCategoriesInternalTitle"
      | "featuredCategoriesTitle"
      | "featuredCategoriesList"
    >
  >(
    // @ts-ignore
    () => {
      if (
        audience?.toLowerCase() ===
        data?.helpCenterHomePageLandingPage?.attendee?.title?.toLowerCase()
      ) {
        return data?.contentfulCategoryMenu?.featuredCategories.find(
          (x) =>
            x.featuredCategoriesInternalTitle.trim().toLowerCase() ===
            "attending an event topics"
        )
      }

      if (
        audience?.toLowerCase() ===
        data?.helpCenterHomePageLandingPage?.organizer?.title?.toLowerCase()
      ) {
        return data?.contentfulCategoryMenu?.featuredCategories.find(
          (x) =>
            x.featuredCategoriesInternalTitle.trim().toLowerCase() ===
            "organizing an event topics"
        )
      }

      if (data?.contentfulCategoryMenu?.featuredCategories?.length <= 0) {
        return null
      }

      return data.contentfulCategoryMenu.featuredCategories[0]
    },
    [data, audience]
  )

  const enhancedAudienceData = useMemo(() => {
    if (
      audienceCategoryData &&
      audienceCategoryData?.featuredCategoriesInternalTitle
    ) {
      return {
        ...audienceCategoryData,
        sidebarTitle:
          audienceCategoryData?.featuredCategoriesInternalTitle
            .toLowerCase()
            .trim() === "attending an event topics"
            ? renderRichText(
                data?.headerAttendeeTopics.microcopyValue?.raw
              )?.[0].props.children[0]
            : audienceCategoryData?.featuredCategoriesInternalTitle
                .toLowerCase()
                .trim() === "organizing an event topics"
            ? renderRichText(
                data?.headerOrganizerTopics?.microcopyValue?.raw
              )?.[0].props.children[0]
            : ""
      }
    }
  }, [audienceCategoryData])

  const featuredCategoriesTitles: GenericPropItem[] = useMemo(() => {
    return data?.contentfulCategoryMenu?.featuredCategories.map((x) => {
      // use headerOrganizerTopics to fetch the "Organizer topics" title
      if (
        x.featuredCategoriesInternalTitle?.toLowerCase().trim() ===
        "organizing an event topics"
      ) {
        return {
          featuredCategoriesInternalTitle: renderRichText(
            data?.headerOrganizerTopics.microcopyValue?.raw
          )[0].props.children[0],
          audienceValue: data?.helpCenterHomePageLandingPage?.organizer?.title
        }
      }

      // use headerAttendeeTopics to fetch the "Attendee topics" title
      if (
        x.featuredCategoriesInternalTitle?.toLowerCase().trim() ===
        "attending an event topics"
      ) {
        return {
          featuredCategoriesInternalTitle: renderRichText(
            data?.headerAttendeeTopics.microcopyValue?.raw
          )[0].props.children[0],
          audienceValue: data?.helpCenterHomePageLandingPage?.attendee?.title
        }
      }

      // default to `attendee`
      return {
        featuredCategoriesInternalTitle: x.featuredCategoriesInternalTitle,
        audienceValue: data?.helpCenterHomePageLandingPage?.attendee?.title
      }
    })
  }, [])

  useEffect(() => {
    if (featuredCategoriesTitles?.length === 1) {
      dispatch(setAudience(featuredCategoriesTitles?.[0]?.audienceValue))
    }
  }, [featuredCategoriesTitles])

  return (
    <Layout
      gatsbyPageProps={pageProps}
      searchBar={{ isSearchBarVisible: true, isResultsBoxShown: true }}
      microCopy={{
        buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
        buttonContactUs: data?.buttonContactUs,
        messageContactUs: data?.messageContactUs,
        upperNavMicroCopySet: data?.upperNavMicroCopySet,
        headerSearchResults: data?.headerSearchResults,
        messageSearchBar: data?.messageSearchBar,
        chatbotUITranslations: data?.chatbotUITranslations
      }}
      footerCopy={data?.contentfulFooter?.footerReference}
      footerNavCopy={
        data?.contentfulFooterNavigation?.FooterNavigationChildItems
      }
    >
      <div className={topicPageWrapper}>
        <div className={mobileWrapper}>
          <Breadcrumbs
            contentfulMicrocopy={data?.navigationHomepage}
            prefixedBasePath={prefixedBasePath}
          />
          <h1 className={header}>
            {data?.contentfulCategoryMenu?.categoryTitle}
          </h1>
          <div className={contentWrapper}>
            {audienceCategoryData && audience ? (
              <CategorySideNav
                prefixedBasePath={prefixedBasePath}
                featuredCategoriesTitle={enhancedAudienceData?.sidebarTitle}
                featuredCategoriesList={
                  enhancedAudienceData?.featuredCategoriesList
                }
                selectedCategory={data?.contentfulCategoryMenu?.categoryTitle}
              />
            ) : (
              <GenericSideNav
                prefixedBasePath={prefixedBasePath}
                iconName={data?.contentfulCategoryMenu?.iconName}
                selectedCategory={data?.contentfulCategoryMenu?.categoryTitle}
                categories={featuredCategoriesTitles}
              />
            )}
            <div>
              {subTopics?.map((subTopic, index) => {
                return (
                  <Subtopic
                    key={subTopic.subcategoryHeader}
                    prefixedBasePath={prefixedBasePath}
                    subcategoryHeader={subTopic.subcategoryHeader}
                    articleList={subTopic.articleList}
                    index={index}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query Categories($categoryMenuSlug: String, $node_locale: String) {
    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    helpCenterHomePageLandingPage: contentfulLandingPage(
      contentful_id: { eq: "7LCib6F4igPPYP0UoJIeA4" }
      node_locale: { eq: $node_locale }
    ) {
      attendee: landingPageAttendee {
        title: landingPageVariationTitle
      }
      organizer: landingPageOrganizer {
        title: landingPageVariationTitle
      }
    }

    allContentfulFeaturedCategories(
      filter: { node_locale: { eq: $node_locale } }
    ) {
      edges {
        node {
          featuredCategoriesInternalTitle
          featuredCategoriesList {
            categoryMenuSlug
            categoryTitle
            iconName: svgIconName
            categoryAudience {
              audience: english
            }
          }
        }
      }
    }

    headerAttendeeTopics: contentfulMicrocopy(
      microcopyInternalTitle: { eq: "Header: Attending an event topics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    headerOrganizerTopics: contentfulMicrocopy(
      microcopyInternalTitle: { eq: "Header: Organizing an event topics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    # BREADCRUMB FIELDS
    navigationHomepage: contentfulMicrocopy(
      microcopyKey: { eq: "navigationHomepage" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    # END BREADCRUMB FIELDS

    # LAYOUT FIELDS
    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageStillHaveQuestions" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }
    # END LAYOUT FIELDS

    contentfulCategoryMenu(
      categoryMenuSlug: { eq: $categoryMenuSlug }
      node_locale: { eq: $node_locale }
    ) {
      categoryMenuSlug
      categoryTitle
      iconName: svgIconName
      featuredCategories: featured_categories {
        featuredCategoriesInternalTitle
        featuredCategoriesTitle
        featuredCategoriesList {
          categoryMenuSlug
          categoryTitle
          iconName: svgIconName
        }
      }
      categoryAudience {
        audience: english
      }
      categoryMenuSeoMetadata {
        seoMetaTagCrawlerFollow
        seoMetaTagDescription
        seoMetaTagRobotsIndex
        seoMetaTagTitle
      }
      subcategories {
        subcategoryHeader
        articleList {
          articleId
          title: helpArticleTitle
          slug
        }
      }
    }
  }
`

export const Head: HeadFC<Queries.CategoriesQuery, PageContext> = (
  headArgs
) => {
  const canonicalUrl = useMemo<string>(() => {
    if (headArgs?.location?.pathname?.length > 0) {
      return headArgs?.location?.pathname
    }

    return ""
  }, [headArgs])

  const hrefLangMetaData = useMemo(
    () => headArgs?.pageContext?.metaData?.hrefLangMetaData,
    [headArgs]
  )

  const shouldNoIndex = useMemo(
    () =>
      !headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
        ?.seoMetaTagRobotsIndex,
    [headArgs?.data?.contentfulCategoryMenu]
  )

  return (
    <>
      <HeadUniversal {...headArgs} 
      metaTitle={headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
              ?.seoMetaTagTitle}
              metaDescription={ headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
                ?.seoMetaTagDescription}
              />
      {headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata && (
        <>
          {getTitleText(
            headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
              ?.seoMetaTagTitle
          )}
          {generateDescriptionMetaTag(
            headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
              ?.seoMetaTagDescription
          )}
          {generateRobotsMetaTag(
            headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
              ?.seoMetaTagRobotsIndex,
            headArgs?.data?.contentfulCategoryMenu?.categoryMenuSeoMetadata
              ?.seoMetaTagCrawlerFollow
          )}
          <HrefLangTags
            metaData={hrefLangMetaData}
            shouldNoIndex={shouldNoIndex}
          />
          <CanonicalTag
            href={canonicalUrl}
            nodeLocale={headArgs?.pageContext?.node_locale}
            shouldNoIndex={shouldNoIndex}
          />
          )
        </>
      )}
    </>
  )
}

export default TopicPageTemplate
